import { DESIGN_SYSTEM_ROOT_MAPPING } from "@/shared/constants";

type DesignSystemInput = Record<string, unknown>;

/** Font keys need the value quoted (or kept as-is for comma-separated stacks). */
const FONT_KEYS = new Set([
  "titleFont",
  "subtitleFont",
  "paragraphFont",
  "displayFont",
  "headingFont",
]);

/** Keys whose hex values get converted to an "r, g, b" triplet so callers
 *  can use them inside `rgba(var(--x), <alpha>)`. */
const RGB_TRIPLET_KEYS = new Set(["mediaShadowColor"]);

/**
 * Boolean design-system keys that need value transformation before they
 * can be written to CSS variables. Skipped in the generic resolver so the
 * dedicated boolean handler in `applyBooleanCssTransforms` is the only
 * writer — prevents a flash of invalid CSS where the raw boolean is
 * written before the transform overwrites it.
 */
export const BOOLEAN_CSS_TRANSFORMS: Record<
  string,
  { cssVar: string; t: string; f: string }
> = {
  mediaBlackAndWhiteFilter: {
    cssVar: "--media-black-and-white-filter",
    t: "grayscale(1)",
    f: "grayscale(0)",
  },
  // Hyperlink style toggles are stored as booleans; the text stylesheet reads
  // them as `font-weight: var(--hyperlinks-bold, inherit)` etc., so a raw
  // `true` would make the whole declaration invalid.
  hyperlinkBold: { cssVar: "--hyperlinks-bold", t: "bold", f: "inherit" },
  hyperlinkItalic: { cssVar: "--hyperlinks-italic", t: "italic", f: "inherit" },
  hyperlinkUnderline: {
    cssVar: "--hyperlinks-underline",
    t: "underline",
    f: "none",
  },
};

/** Boolean transform pairs as `[cssVar, value]`, for string emitters. */
export const resolveBooleanCssTransforms = (
  design: Record<string, unknown>,
): Array<[cssVar: string, cssValue: string]> => {
  const pairs: Array<[string, string]> = [];
  applyBooleanCssTransforms(design, (cssVar, value) =>
    pairs.push([cssVar, value]),
  );
  return pairs;
};

/**
 * Apply every boolean CSS-variable transform described in
 * `BOOLEAN_CSS_TRANSFORMS` to the given setter. Consumers pass a setter
 * that writes to either `document.documentElement` or a scoped container.
 */
export const applyBooleanCssTransforms = (
  design: Record<string, unknown>,
  setProperty: (cssVar: string, value: string) => void,
) => {
  for (const [key, transform] of Object.entries(BOOLEAN_CSS_TRANSFORMS)) {
    const raw = design[key];
    // Older stories persisted these flags as the strings "true" / "false".
    const value = raw === "true" ? true : raw === "false" ? false : raw;
    if (typeof value === "boolean") {
      setProperty(transform.cssVar, value ? transform.t : transform.f);
    } else if (typeof value === "string" && value.trim()) {
      // A ready CSS value (e.g. a legacy "underline") is written as-is.
      setProperty(transform.cssVar, value);
    }
  }
};

const hexToRgbTriplet = (hex: string): string => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  return `${Number.parseInt(full.slice(0, 2), 16)}, ${Number.parseInt(
    full.slice(2, 4),
    16,
  )}, ${Number.parseInt(full.slice(4, 6), 16)}`;
};

/**
 * When a secondary button key is missing from the design system, we
 * chain its CSS variable to the primary equivalent so the secondary
 * button still picks up a usable value. This avoids the "invalid at
 * computed-value time" fallback where the browser silently resolves
 * `var(--unset)` to the property's initial value (e.g. `background:
 * none`).
 */
const SECONDARY_TO_PRIMARY_FALLBACK: Record<
  string,
  { primaryKey: string; primaryVar: string }
> = {
  secondaryButtonColor: {
    primaryKey: "buttonColor",
    primaryVar: "--button-text-color",
  },
  secondaryButtonBackgroundColor: {
    primaryKey: "buttonBackgroundColor",
    primaryVar: "--button-background-color",
  },
  secondaryButtonBorderColor: {
    primaryKey: "buttonBorderColor",
    primaryVar: "--button-border-color",
  },
  secondaryButtonRadius: {
    primaryKey: "buttonRadius",
    primaryVar: "--button-border-radius",
  },
  secondaryButtonBorderWidth: {
    primaryKey: "buttonBorderWidth",
    primaryVar: "--button-border-width",
  },
};

/**
 * Hover-color pairs with automatic fallback. When the user hasn't set an
 * explicit hover color, we derive it from the paired base color at
 * `HOVER_FALLBACK_OPACITY` so buttons / links still get a visible hover
 * state instead of an invisible one. `baseFallbackKey` is used when the
 * direct base is missing too — relevant for secondary buttons that
 * should inherit from the primary when not customised.
 */
const HOVER_FALLBACK_PAIRS: Record<
  string,
  { baseKey: string; baseFallbackKey?: string; hoverVar: string }
> = {
  buttonHoverColor: {
    baseKey: "buttonColor",
    hoverVar: "--button-text-hover-color",
  },
  buttonBackgroundHoverColor: {
    baseKey: "buttonBackgroundColor",
    hoverVar: "--button-background-hover-color",
  },
  buttonBorderColorHover: {
    baseKey: "buttonBorderColor",
    hoverVar: "--button-border-color-hover",
  },
  secondaryButtonHoverColor: {
    baseKey: "secondaryButtonColor",
    baseFallbackKey: "buttonColor",
    hoverVar: "--secondary-button-text-hover-color",
  },
  secondaryButtonBackgroundHoverColor: {
    baseKey: "secondaryButtonBackgroundColor",
    baseFallbackKey: "buttonBackgroundColor",
    hoverVar: "--secondary-button-background-hover-color",
  },
  secondaryButtonBorderColorHover: {
    baseKey: "secondaryButtonBorderColor",
    baseFallbackKey: "buttonBorderColor",
    hoverVar: "--secondary-button-border-color-hover",
  },
  hyperlinkHoverColor: {
    baseKey: "hyperlinkColor",
    hoverVar: "--hyperlinks-text-hover-color",
  },
};

export const HOVER_FALLBACK_OPACITY = 0.7;

/**
 * Convert a 3- or 6-digit hex color into an `rgba()` string at the given
 * alpha. Returns `null` for anything that isn't a parseable hex color so
 * callers can cleanly skip unknown shapes (CSS var references, empty
 * strings, etc.) without silently producing broken CSS.
 */
export const hexToRgba = (hex: unknown, alpha: number): string | null => {
  if (typeof hex !== "string" || !hex.startsWith("#")) return null;
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  if (full.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Given a raw hover color and its base counterpart, return the CSS value
 * the hover slot should use. Explicit non-empty hover wins; otherwise
 * fall back to the base at `HOVER_FALLBACK_OPACITY`; otherwise `null`
 * (nothing to write).
 */
export const deriveHoverColor = (
  hoverValue: unknown,
  baseValue: unknown,
): string | null => {
  if (typeof hoverValue === "string" && hoverValue.trim().length > 0) {
    return hoverValue;
  }
  return hexToRgba(baseValue, HOVER_FALLBACK_OPACITY);
};

const quoteFontValue = (raw: string): string => {
  const trimmed = raw.trim();
  if (trimmed.includes(",")) return trimmed;
  const isQuoted = trimmed.startsWith('"') && trimmed.endsWith('"');
  return `${isQuoted ? trimmed : `"${trimmed}"`}, sans-serif`;
};

/**
 * Turn a design-system object into the ordered list of CSS custom-property
 * `[name, value]` pairs that should be written to the document root.
 *
 * Shared by `handleDesign` (live editor / design-system dialog preview)
 * and `handleDesignSystem` (rendering-provider template strings). The
 * pure-data shape keeps both consumers decoupled from how the CSS gets
 * emitted (DOM API vs string concatenation).
 */
export const resolveDesignSystemVariables = (
  design: DesignSystemInput,
): Array<[cssVar: string, cssValue: string]> => {
  const pairs: Array<[string, string]> = [];
  // Names written so far. Chaining a variable to one we never write leaves the
  // chained variable DEFINED but invalid at computed-value time, which silently
  // defeats every downstream `var(--x, fallback)` — the fallback is skipped
  // because the variable exists. Guarding both chains below on this set keeps
  // the invariant "never reference a variable this function did not write".
  const written = new Set<string>();
  const push = (name: string, value: string) => {
    pairs.push([name, value]);
    written.add(name);
  };

  for (const [dsKey, cssVar] of Object.entries(DESIGN_SYSTEM_ROOT_MAPPING)) {
    // Hover keys are handled in the dedicated pass below so they can
    // fall back to a derived rgba when the user has no explicit value.
    if (dsKey in HOVER_FALLBACK_PAIRS) continue;
    // Boolean keys are handled by `applyBooleanCssTransforms`. Skipping
    // them here avoids a write-then-overwrite flash where `String(true)`
    // would land in the CSS var for a frame before being replaced.
    if (dsKey in BOOLEAN_CSS_TRANSFORMS) continue;
    const value = design[dsKey];
    if (value !== undefined && value !== null) {
      let stringValue: string;
      if (FONT_KEYS.has(dsKey) && typeof value === "string") {
        stringValue = quoteFontValue(value);
      } else if (
        RGB_TRIPLET_KEYS.has(dsKey) &&
        typeof value === "string" &&
        value.startsWith("#")
      ) {
        stringValue = hexToRgbTriplet(value);
      } else {
        stringValue = String(value);
      }
      push(cssVar, stringValue);
      continue;
    }
    // Secondary button keys chain to their primary equivalent when the
    // user hasn't customised them so the secondary button still renders
    // with a usable value instead of an unresolved CSS variable.
    const fallback = SECONDARY_TO_PRIMARY_FALLBACK[dsKey];
    // Only chain when the primary is actually going to be written. Chaining to
    // a variable that is itself never set leaves the secondary variable
    // DEFINED but invalid at computed-value time, which is worse than leaving
    // it unset: a downstream `var(--secondary-button-border-width, 1px)` sees a
    // defined variable, skips its own fallback, and resolves to nothing — so
    // the border disappeared instead of falling back. A design system with no
    // primary border keys (most of them predate those controls) hit this on
    // every secondary button.
    if (fallback && written.has(fallback.primaryVar)) {
      push(cssVar, `var(${fallback.primaryVar})`);
    }
  }

  for (const [
    hoverKey,
    { baseKey, baseFallbackKey, hoverVar },
  ] of Object.entries(HOVER_FALLBACK_PAIRS)) {
    const hoverValue = design[hoverKey];
    if (typeof hoverValue === "string" && hoverValue.trim().length > 0) {
      push(hoverVar, hoverValue);
      continue;
    }

    // Try deriving from the direct base first, then from the fallback
    // base (used by secondary buttons that should inherit from the
    // primary when the secondary base is missing).
    let resolved = hexToRgba(design[baseKey], HOVER_FALLBACK_OPACITY);
    if (resolved === null && baseFallbackKey) {
      resolved = hexToRgba(design[baseFallbackKey], HOVER_FALLBACK_OPACITY);
    }
    if (resolved !== null) {
      push(hoverVar, resolved);
      continue;
    }

    // Last resort for secondary hover keys: chain the hover CSS var to
    // the primary hover CSS var so the secondary button still gets a
    // valid value at browser render time.
    const baseFallback = baseFallbackKey
      ? SECONDARY_TO_PRIMARY_FALLBACK[baseKey]
      : undefined;
    if (baseFallback) {
      // Match the hover slot on the primary side, e.g. buttonBorderColor
      // → --button-border-color-hover for border, buttonColor →
      // --button-text-hover-color for text, etc. We already encoded the
      // primary hover CSS var in HOVER_FALLBACK_PAIRS for the primary
      // keys, so look it up by matching the primaryKey.
      const primaryHoverEntry = Object.values(HOVER_FALLBACK_PAIRS).find(
        (entry) => entry.baseKey === baseFallback.primaryKey,
      );
      // Only chain when the primary hover variable was actually written —
      // it is derived from the primary base colour, which a design system
      // without primary border keys does not have.
      if (primaryHoverEntry && written.has(primaryHoverEntry.hoverVar)) {
        push(hoverVar, `var(${primaryHoverEntry.hoverVar})`);
      }
    }
  }

  return pairs;
};
